/**
 * Validation utilities for configuration and input
 */

import { z } from 'zod';
import { ValidationError, ConfigurationError } from '@/types';
import { logger } from '@/utils/logger';

/**
 * Validation result type
 */
export interface ValidationResult<T> {
  success: boolean;
  data?: T;
  errors?: string[];
}

/**
 * Configuration validation utilities
 */
export class ValidationUtils {
  /**
   * Validate data against a Zod schema with user-friendly error messages
   */
  static validateSchema<T>(
    schema: z.ZodSchema<T>,
    data: unknown,
    context: string
  ): ValidationResult<T> {
    try {
      const validated = schema.parse(data);
      return {
        success: true,
        data: validated,
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors = error.issues.map(issue => {
          const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
          return `${path}: ${issue.message}`;
        });

        return {
          success: false,
          errors,
        };
      }

      return {
        success: false,
        errors: [`Unexpected validation error in ${context}: ${String(error)}`],
      };
    }
  }

  /**
   * Validate and throw appropriate error with context
   */
  static validateOrThrow<T>(
    schema: z.ZodSchema<T>,
    data: unknown,
    context: string
  ): T {
    const result = this.validateSchema(schema, data, context);

    if (!result.success) {
      throw new ValidationError(
        `Validation failed for ${context}: ${result.errors?.join(', ')}`
      );
    }

    return result.data as T;
  }

  /**
   * Validate API token format with detailed feedback
   */
  static validateApiToken(token: string): void {
    if (!token) {
      throw new ValidationError('API token is required');
    }

    if (token.length < 10) {
      throw new ValidationError('API token is too short');
    }

    const tokenPattern = /^trx_(development|staging|production)_[a-f0-9]{64}$/;
    if (!tokenPattern.test(token)) {
      const parts = token.split('_');

      if (parts.length !== 3) {
        throw new ValidationError(
          'API token must have 3 parts separated by underscores: trx_{environment}_{key}'
        );
      }

      const prefix = parts[0];
      const environment = parts[1];
      const key = parts[2];

      if (prefix !== 'trx') {
        throw new ValidationError(
          `API token must start with "trx", found "${prefix}"`
        );
      }

      if (
        !environment ||
        !['development', 'staging', 'production'].includes(environment)
      ) {
        throw new ValidationError(
          `Invalid environment "${environment || 'empty'}". Must be: development, staging, or production`
        );
      }

      if (!key || !/^[a-f0-9]{64}$/.test(key)) {
        throw new ValidationError(
          'API token key must be 64 lowercase hexadecimal characters'
        );
      }
    }
  }

  /**
   * Validate URL format with helpful feedback
   */
  static validateUrl(url: string, context: string): void {
    if (!url) {
      throw new ValidationError(`${context} URL is required`);
    }

    try {
      const parsed = new URL(url);

      if (!['http:', 'https:'].includes(parsed.protocol)) {
        throw new ValidationError(
          `${context} URL must use HTTP or HTTPS protocol, found: ${parsed.protocol}`
        );
      }

      if (!parsed.hostname) {
        throw new ValidationError(`${context} URL must have a valid hostname`);
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }
      throw new ValidationError(`Invalid ${context} URL format: ${url}`);
    }
  }

  /**
   * Validate file path exists and is accessible
   */
  static validateFilePath(path: string, context: string): void {
    if (!path) {
      throw new ValidationError(`${context} path is required`);
    }

    if (path.includes('..')) {
      throw new ValidationError(
        `${context} path cannot contain parent directory references (..)`
      );
    }

    if (path.startsWith('/') && !process.platform.startsWith('win')) {
      logger.warn(`Using absolute path for ${context}: ${path}`);
    }
  }

  /**
   * Validate Node.js version requirement
   */
  static validateNodeVersion(requiredVersion: string = '18.0.0'): void {
    const currentVersion = process.version;
    const current = this.parseVersion(currentVersion.slice(1)); // Remove 'v' prefix
    const required = this.parseVersion(requiredVersion);

    if (this.compareVersions(current, required) < 0) {
      throw new ConfigurationError(
        `Node.js ${requiredVersion} or higher is required. ` +
          `Current version: ${currentVersion}`
      );
    }
  }

  /**
   * Parse semantic version string
   */
  private static parseVersion(version: string): [number, number, number] {
    const parts = version.split('.').map(Number);
    return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
  }

  /**
   * Compare two semantic versions
   * Returns: -1 if a < b, 0 if a === b, 1 if a > b
   */
  private static compareVersions(
    a: [number, number, number],
    b: [number, number, number]
  ): number {
    for (let i = 0; i < 3; i++) {
      const aVal = a[i] ?? 0;
      const bVal = b[i] ?? 0;
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
    }
    return 0;
  }

  /**
   * Validate environment variable name
   */
  static validateEnvVarName(name: string): void {
    if (!name) {
      throw new ValidationError('Environment variable name is required');
    }

    if (!/^[A-Z][A-Z0-9_]*$/.test(name)) {
      throw new ValidationError(
        `Environment variable name "${name}" must be uppercase letters, numbers, and underscores only`
      );
    }
  }

  /**
   * Sanitize and validate file size
   */
  static validateFileSize(
    size: number,
    maxSize: number,
    filename: string
  ): void {
    if (size < 0) {
      throw new ValidationError(`Invalid file size for ${filename}: ${size}`);
    }

    if (size > maxSize) {
      const sizeMB = Math.round(size / 1024 / 1024);
      const maxSizeMB = Math.round(maxSize / 1024 / 1024);

      throw new ValidationError(
        `File ${filename} is too large: ${sizeMB}MB (max: ${maxSizeMB}MB)`
      );
    }
  }

  /**
   * Validate timeout value
   */
  static validateTimeout(timeout: number): void {
    if (timeout <= 0) {
      throw new ValidationError('Timeout must be greater than 0');
    }

    if (timeout > 300000) {
      // 5 minutes
      logger.warn(`Large timeout value: ${timeout}ms (${timeout / 1000}s)`);
    }
  }

  /**
   * Validate retry count
   */
  static validateRetryCount(retries: number): void {
    if (retries < 0) {
      throw new ValidationError('Retry count cannot be negative');
    }

    if (retries > 10) {
      logger.warn(`High retry count: ${retries} (this may cause long delays)`);
    }
  }
}
